import React from "react";
import ProfileContainer from "./ProfileContainer";
import useBoolean from "../hooks/useBoolean";
import { useShallow } from "zustand/react/shallow";
import useInvestorAmlDocumentStore from "../zstore/investorAmlDocument";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import useRequiredEvidenceTypesStore from "../zstore/requiredEvidenceTypes";
import AmlDocument from "../components/AmlDocument";
import { InvestorAmlDocument } from "../api/transformers/document";
import { EmptyPage } from "iconoir-react";
import Text from "../components/Text";
import { formatDate } from "../utils/date";
import FilePreview from "../components/FilePreview";
import { LoadingButton } from "@mui/lab";

type ReviewDocumentsProfileProps = {
  personId: string;
};

const ReviewDocumentsProfile: React.FC<ReviewDocumentsProfileProps> = ({
  personId,
}) => {
  const { loading, documents } = useInvestorAmlDocumentStore(
    useShallow((state) => ({ loading: state.loading, documents: state.data }))
  );

  return (
    <ProfileContainer
      id="review-document"
      title="Review Documents"
      loading={loading}
    >
      <Stack direction="column" rowGap="6px">
        {documents.length === 0 ? (
          <Typography>No documents</Typography>
        ) : (
          documents.map((el) => <DocumentOverview personId={personId} key={el.urn} data={el} />)
        )}
      </Stack>
    </ProfileContainer>
  );
};

const DocumentOverview: React.FC<{ personId: string; data: InvestorAmlDocument }> = ({
  personId,
  data,
}) => {
  const { getAmlCategory } = useRequiredEvidenceTypesStore(personId);
  const showCategory = data.otherTypeDescription || data.amlTypeCode;
  return (
    <AmlDocument.Root key={data.urn}>
      <AmlDocument.Status status="SUCCESS" />
      <AmlDocument.Property
        label="File properties"
        sx={{ width: "160px", gap: "2px" }}
      >
        <Box
          style={{
            display: "flex",
            width: "100%",
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          <Box
            style={{
              display: "flex",
              width: "100%",
              gap: "4px",
            }}
          >
            <EmptyPage
              style={{
                fill: "#7e7e7e",
                strokeWidth: "0",
                fontSize: "0.9rem",
                flexShrink: 0,
              }}
            />
            <Tooltip title={data.filename} placement="top">
              <Text ellipsis={1}>{data.filename}</Text>
            </Tooltip>
          </Box>
        </Box>
      </AmlDocument.Property>
      <AmlDocument.Property label="Upload Time" sx={{ gap: "2px" }}>
        <Box style={{ display: "flex", flexDirection: "column", gap: "3px" }}>
          <Text>{formatDate(data.uploadTime)}</Text>
        </Box>
      </AmlDocument.Property>
      {showCategory && (
        <AmlDocument.Property label="Category" sx={{ gap: "2px" }}>
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "3px",
            }}
          >
            {data.otherTypeDescription ? (
              <Text ellipsis={1} italic>{`${data.otherTypeDescription}`}</Text>
            ) : (
              <Text>
                {getAmlCategory(data.amlTypeCode)?.description ||
                  data.amlTypeCode}
              </Text>
            )}
          </Box>
        </AmlDocument.Property>
      )}
      <AmlDocument.Controller
        width="100px"
        sx={{ height: "100%", justifyContent: "center" }}
      >
        <FilePreview filename={data.filename}>
          {({ previewRemoteFile, loading }) => {
            return (
              <LoadingButton
                onClick={() => previewRemoteFile(data.urn)}
                loading={loading}
                variant="outlined"
                sx={{ borderRadius: "4px", padding: "3px 9px" }}
              >
                VIEW
              </LoadingButton>
            );
          }}
        </FilePreview>
      </AmlDocument.Controller>
    </AmlDocument.Root>
  );
};

export default ReviewDocumentsProfile;
